import React, { useState, useEffect, useContext } from "react";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { BsImage } from "react-icons/bs";
import { getStorage, ref, getDownloadURL, uploadBytes } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../js/firebase";
import { OrganizationUsers } from "./organization-users";
import { ProfileContext } from "../contexts/ProfileContext";
import { showErrorMessage, showSuccessMessage } from "../helpers/useToast";
import { OrganizationContext } from "../contexts/OrganizationContext";
import {
    organizationDenominations,
    organizationTiers,
    organizationTypes,
    countries,
    paymentTerms,
    provinces,
} from "../assets/data";
import {
    isValidEmail,
    isValidPhone,
    isValidPostalCode,
    isValidWebsite,
    isValidAddress,
    exceedsWordLimit,
} from "../helpers/organizationValidation";

export const OrganizationDetailsModal = ({ organization, show, handleClose }) => {
    const { worker } = useContext(ProfileContext) || {};
    const canEdit = worker?.role?.includes("admin") || worker?.role?.includes("super_admin");
    const { filterOrganizations, filterByArray, filterValueArray, fetchCallMetrics } = useContext(OrganizationContext);

    const [organizationImage, setOrganizationImage] = useState(null);
    const [isImageLoading, setIsImageLoading] = useState(false);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState(null);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [auth0PrayerPartnerCount, setAuth0PrayerPartnerCount] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    
    // Effect to load the organization image when organization changes
    useEffect(() => {
        if (!organization) return;
        // initialize form when organization changes
        setForm({ ...organization });

        const loadPrayerPartnerCount = async () => {
            if (!organization.id) return;

            const result = await fetchCallMetrics(organization.id, worker?.role);
            const metrics = result?.data?.original?.data;
            const organizationMetrics = Array.isArray(metrics)
                ? metrics.find((item) => item.firebase_id === organization.id)
                : null;

            setAuth0PrayerPartnerCount(organizationMetrics?.volunteers ?? 0);
        };

        loadPrayerPartnerCount();

        const loadOrganizationImage = async () => {
            setIsImageLoading(true);
            try {
                // Prefer a stored direct URL if available (new flow writes `logoUrl`)
                if (organization.logoUrl) {
                    setOrganizationImage(organization.logoUrl);
                    return;
                }

                const storage = getStorage();

                // Try by document id (new uploads use docId.ext)
                const tryExts = ["png", "jpg", "jpeg", "webp", "svg"];
                if (organization.id) {
                    for (const ext of tryExts) {
                        try {
                            const refById = ref(storage, `logos/${organization.id}.${ext}`);
                            const url = await getDownloadURL(refById);
                            setOrganizationImage(url);
                            return;
                        } catch (err) {
                            // ignore and try next extension
                        }
                    }
                }

                // Fallback: try by organization name (legacy uploads used the name)
                if (organization.name) {
                    for (const ext of tryExts) {
                        try {
                            const refByName = ref(storage, `logos/${organization.name}.${ext}`);
                            const url = await getDownloadURL(refByName);
                            setOrganizationImage(url);
                            return;
                        } catch (err) {
                            // also try without extension for exact match
                        }
                    }

                    // try without extension (some uploads used .png by default without extension handling)
                    try {
                        const refNoExt = ref(storage, `logos/${organization.name}.png`);
                        const url = await getDownloadURL(refNoExt);
                        setOrganizationImage(url);
                        return;
                    } catch (err) {
                        // no image found
                    }
                }

                // Nothing found
                setOrganizationImage(null);
            } catch (error) {
                console.error("Error loading organization image:", error);
                setOrganizationImage(null);
            } finally {
                setIsImageLoading(false);
            }
        };

        loadOrganizationImage();
    // fetchCallMetrics is provided by context and is intentionally omitted because its identity changes on provider renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization, worker?.role]);

    // Helper to update form fields
    const updateField = (field, value) => {
        setForm((f) => ({ ...f, [field]: value }));
        setFieldErrors((errors) => ({ ...errors, [field]: "" }));
    };

    const handleSave = async () => {
        if (!form || !form.id) return;

        const errors = {};
        if (!isValidPhone(form.organizationPhoneNumber)) {
            errors.organizationPhoneNumber = "Enter a valid phone number.";
        }
        if (form.carePersonPhone && !isValidPhone(form.carePersonPhone)) {
            errors.carePersonPhone = "Enter a valid phone number.";
        }

        if (form.carePersonEmail && !isValidEmail(form.carePersonEmail)) {
            errors.carePersonEmail = "Enter a valid email address.";
        }

        if (form.website && !isValidWebsite(form.website)) {
            errors.website = "Enter a valid URL.";
        }

        if (!isValidPostalCode(form.postalCode)) {
            errors.postalCode = "Enter a valid postal code.";
        }
        if (!isValidAddress(form.address)) {
            errors.address = "Enter a complete address with a street number.";
        }
        for (const field of ["serviceStartTimes", "churchPrograms", "description"]) {
            if (exceedsWordLimit(form[field])) {
                errors[field] = "Maximum 500 words.";
            }
        }
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            showErrorMessage("Please correct the highlighted fields");
            return;
        }

        try {
            const docRef = doc(db, "organizations", form.id);
            const toSave = { ...form };
            // prevent updating createdAt here
            delete toSave.createdAt;
            delete toSave.prayerPartnersCount;
            await updateDoc(docRef, toSave);
            showSuccessMessage("Organization updated successfully");
            setEditing(false);
            setFieldErrors({});
            // refresh organizations list
            try {
              await filterOrganizations(filterByArray, filterValueArray);
            } catch (e) {
              console.error('Failed to refresh organizations after save', e);
            }
        } catch (err) {
            console.error("Error saving organization:", err);
            showErrorMessage("Failed to save organization");
        }
    };

    const handleLogoChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file || !form?.id) return;
        setIsUploadingLogo(true);
        try {
            const storage = getStorage();
            const ext = file.name.split('.').pop() || 'png';
            const filename = `${form.id}.${ext}`;
            const storageRef = ref(storage, `logos/${filename}`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            setOrganizationImage(url);
            setForm((f) => ({ ...f, logoUrl: url }));
            showSuccessMessage('Logo uploaded');
            // Also update doc immediately so modal shows latest
            try {
              const docRef = doc(db, 'organizations', form.id);
              await updateDoc(docRef, { logoUrl: url });
              // refresh list
              await filterOrganizations(filterByArray, filterValueArray);
            } catch (err) {
              console.error('Failed to update doc with logoUrl', err);
            }
        } catch (err) {
            console.error('Logo upload failed', err);
            showErrorMessage('Failed to upload logo');
        } finally {
            setIsUploadingLogo(false);
            // clear input value
            e.target.value = null;
        }
    };
    
    // Format createdAt date if it exists
    const formatDate = (dateValue) => {
        if (!dateValue) return "";
        
        try {
            // Handle Firestore timestamp
            if (dateValue.seconds) {
                return new Date(dateValue.seconds * 1000).toLocaleString();
            }
            // Handle regular Date object
            if (dateValue instanceof Date) {
                return dateValue.toLocaleString();
            }
            // Handle string date
            return new Date(dateValue).toLocaleString();
        } catch (error) {
            console.error("Error formatting date:", error);
            return "Invalid date";
        }
    };
    
    if (!organization) return null;
    
    return (
        <Modal show={show} onHide={handleClose} dialogClassName="modal-90w">
            <Modal.Header closeButton>
                <Modal.Title>
                    {editing ? `Edit: ${form?.name || organization.name}` : `Viewing: ${organization.name}`}
                </Modal.Title>
                {canEdit && (
                  <div style={{ marginLeft: '12px' }}>
                    {!editing ? (
                      <Button variant="outline-secondary" onClick={() => setEditing(true)}>Edit</Button>
                    ) : (
                      <>
                        <Button variant="primary" onClick={handleSave} style={{ marginRight: 8 }}>Save</Button>
                        <Button variant="secondary" onClick={() => { setEditing(false); setForm({ ...organization }); }}>Cancel</Button>
                      </>
                    )}
                  </div>
                )}
            </Modal.Header>
            <Modal.Body>
                <div className="flex justify-center"> 
                    {isImageLoading ? (
                        <div className="flex items-center justify-center w-[146px] h-[146px]">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2DB3C0]"></div>
                        </div>
                    ) : organizationImage ? (
                        <img src={organizationImage} alt="logo" width={146}/> 
                    ) : (
                        <BsImage size={146} className="text-gray-400"/>     
                    )}
                </div>
                {canEdit && editing && (
                  <div className="text-center mt-2 mb-3">
                    <input type="file" accept="image/*" id="logoUpload" onChange={handleLogoChange} style={{ display: 'inline-block' }} />
                    {isUploadingLogo && <div className="text-sm text-gray-500">Uploading...</div>}
                  </div>
                )}

                <div className="p-3">
                    <h4 className="p-2 text-left text-[#FF7234] font-['Recoleta'] underline">
                        Organization Info
                    </h4>
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="organizationName"><h6>Organization Name</h6></label>
                            {editing ? (
                                <input type="text" className="form-control" id="organizationName" value={form?.name || ""} onChange={(e) => updateField('name', e.target.value)} />
                            ) : (
                                <input type="text" className="form-control" id="organizationName" value={organization.name || ""} readOnly/>
                            )}
                        </div>

                        <div className="col">
                            <label htmlFor="organizationType"><h6>Organization Type</h6></label>
                            {editing ? (
                                <select className="form-control" id="organizationType" value={form?.type || ""} onChange={(e) => updateField('type', e.target.value)}>
                                    <option value="">Select Type</option>
                                    {organizationTypes.map((option) => <option key={option.id} value={option.value}>{option.label}</option>)}
                                </select>
                            ) : (
                                <input type="text" className="form-control" id="organizationType" value={organization.type || ""} readOnly/>
                            )}
                        </div>
                    </div>
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="organizationDenomination"><h6>Denomination</h6></label>
                            {editing ? (
                                <select className="form-control" id="organizationDenomination" value={form?.denomination || ""} onChange={(e) => updateField('denomination', e.target.value)}>
                                    <option value="">Select Denomination</option>
                                    {organizationDenominations.map((option) => <option key={option.id} value={option.value}>{option.label}</option>)}
                                </select>
                            ) : (
                                <input type="text" className="form-control" id="organizationDenomination" value={organization.denomination || ""} readOnly />
                            )}
                        </div>
                        <div className="col">
                            <label htmlFor="organizationTier"><h6>Tier</h6></label>
                            {editing ? (
                                <select className="form-control" id="organizationTier" value={form?.tier || ""} onChange={(e) => updateField('tier', e.target.value)}>
                                    <option value="">Select Tier</option>
                                    {organizationTiers.map((option) => <option key={option.id} value={option.value}>{option.label}</option>)}
                                </select>
                            ) : (
                                <input type="text" className="form-control" id="organizationTier" value={organization.tier || ""} readOnly />
                            )}
                        </div>
                    </div>
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="createdAt"><h6>Created At</h6></label>
                            <input type="text" className="form-control bg-[#e9ecef]" id="createdAt" value={formatDate(organization.createdAt)} disabled />
                        </div>
                        <div className="col">
                            <label htmlFor="address"><h6>Payment Term</h6></label>
                            {editing ? (
                                <select className="form-control" id="paymentTerm" value={form?.paymentTerm || ""} onChange={(e) => updateField('paymentTerm', e.target.value)}>
                                    <option value="">Select Payment Term</option>
                                    {paymentTerms.map((option) => <option key={option.id} value={option.value}>{option.label}</option>)}
                                </select>
                            ) : (
                                <input type="text" className="form-control bg-[#e9ecef]" id="paymentTerm" value={organization.paymentTerm || ""} disabled />
                            )}
                        </div>
                    </div>
                    <div>
                        <div className="col">
                            <label htmlFor="address"><h6>Address</h6></label>
                            {editing ? (
                                <input type="text" className={`form-control ${fieldErrors.address ? "is-invalid" : ""}`} id="address" placeholder="123 Main Street" value={form?.address || ""} onChange={(e) => updateField('address', e.target.value)} />
                            ) : (
                                <input type="text" className="form-control" id="address" value={organization.address || ""} readOnly/>
                            )}
                            {editing && fieldErrors.address && <div className="invalid-feedback">{fieldErrors.address}</div>}
                        </div>
                    </div>
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="city"><h6>City</h6></label>
                            {editing ? (
                                <input type="text" className="form-control" id="city" value={form?.city || ""} onChange={(e) => updateField('city', e.target.value)} />
                            ) : (
                                <input type="text" className="form-control" id="city" value={organization.city || ""} readOnly />
                            )}
                        </div>
                        <div className="col">
                            <label htmlFor="province"><h6>Province / State</h6></label>
                            {editing ? (
                                <select className="form-control" id="province" value={form?.province || ""} onChange={(e) => updateField('province', e.target.value)} disabled={!form?.country}>
                                    <option value="">Select Province/State</option>
                                    {(provinces[form?.country] || []).map((option) => <option key={option.id} value={option.value}>{option.label}</option>)}
                                </select>
                            ) : (
                                <input type="text" className="form-control" id="province" value={organization.province || ""} readOnly/>
                            )}
                        </div>
                    </div>
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="postalCode"><h6>ZIP / Postal Code</h6></label>
                            {editing ? (
                                <input type="text" className={`form-control ${fieldErrors.postalCode ? "is-invalid" : ""}`} id="postalCode" placeholder="A1A 1A1 or 12345" value={form?.postalCode || ""} onChange={(e) => updateField('postalCode', e.target.value)} />
                            ) : (
                                <input type="text" className="form-control" id="postalCode" value={organization.postalCode || ""} readOnly/>
                            )}
                            {editing && fieldErrors.postalCode && <div className="invalid-feedback">{fieldErrors.postalCode}</div>}
                        </div>
                        <div className="col">
                            <label htmlFor="country"><h6>Country</h6></label>
                            {editing ? (
                                <select className="form-control" id="country" value={form?.country || ""} onChange={(e) => { updateField('country', e.target.value); updateField('province', ''); }}>
                                    <option value="">Select Country</option>
                                    {countries.map((option) => <option key={option.id} value={option.value}>{option.label}</option>)}
                                </select>
                            ) : (
                                <input type="text" className="form-control" id="country" value={organization.country || ""} readOnly/>
                            )}
                        </div>
                    </div>
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="website"><h6>Organization Website</h6></label>
                            {editing ? (
                                <input type="url" className={`form-control ${fieldErrors.website ? "is-invalid" : ""}`} id="website" placeholder="https://example.com" value={form?.website || ""} onChange={(e) => updateField('website', e.target.value)} />
                            ) : (
                                <input type="text" className="form-control" id="website" value={organization.website || ""} readOnly/>
                            )}
                            {editing && fieldErrors.website && <div className="invalid-feedback">{fieldErrors.website}</div>}
                        </div>

                        <div className="col">
                            <label htmlFor="organizationPhoneNumber"><h6>Organization Phone Number</h6></label>
                            {editing ? (
                                <input type="tel" className={`form-control ${fieldErrors.organizationPhoneNumber ? "is-invalid" : ""}`} id="organizationPhoneNumber" placeholder="+1 (555) 555-5555" value={form?.organizationPhoneNumber || ""} onChange={(e) => updateField('organizationPhoneNumber', e.target.value)} />
                            ) : (
                                <input type="text" className="form-control" id="organizationPhoneNumber" value={organization.organizationPhoneNumber || ""} readOnly/>
                            )}
                            {editing && fieldErrors.organizationPhoneNumber && <div className="invalid-feedback">{fieldErrors.organizationPhoneNumber}</div>}
                        </div>
                    </div>
                </div>

                <div className="p-3 bg-[#f3f9fa]">
                    <h4 className="p-2 text-left text-[#2DB3C0] font-['Recoleta'] underline">
                        Person Info
                    </h4>              
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="carePersonName"><h6>Care Person Full Name</h6></label>
                            {editing ? (
                                <input type="text" className="form-control bg-[#f3f9fa]" id="carePersonName" value={form?.carePersonName || ""} onChange={(e) => updateField('carePersonName', e.target.value)} />
                            ) : (
                                <input type="text" className="form-control bg-[#f3f9fa]" id="carePersonName" value={organization.carePersonName || ""} readOnly />
                            )}
                        </div>
                    </div>
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="carePersonEmail"><h6>Care Person Email</h6></label>
                            {editing ? (
                                <input type="email" className={`form-control bg-[#f3f9fa] ${fieldErrors.carePersonEmail ? "is-invalid" : ""}`} id="carePersonEmail" placeholder="name@example.com" value={form?.carePersonEmail || ""} onChange={(e) => updateField('carePersonEmail', e.target.value)} />
                            ) : (
                                <input type="email" className="form-control bg-[#f3f9fa]" id="carePersonEmail" value={organization.carePersonEmail || ""} readOnly />
                            )}
                            {editing && fieldErrors.carePersonEmail && <div className="invalid-feedback">{fieldErrors.carePersonEmail}</div>}
                        </div>
                    </div>
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="carePersonPhone"><h6>Care Person Phone EXT</h6></label>
                            {editing ? (
                                <input type="tel" className={`form-control bg-[#f3f9fa] ${fieldErrors.carePersonPhone ? "is-invalid" : ""}`} id="carePersonPhone" placeholder="+1 (555) 555-5555 ext. 123" value={form?.carePersonPhone || ""} onChange={(e) => updateField('carePersonPhone', e.target.value)} />
                            ) : (
                                <input type="text" className="form-control bg-[#f3f9fa]" id="carePersonPhone" value={organization.carePersonPhone || ""} readOnly />
                            )}
                            {editing && fieldErrors.carePersonPhone && <div className="invalid-feedback">{fieldErrors.carePersonPhone}</div>}
                        </div>
                    </div>
                </div>

                    <div className="p-3 bg-[#FFEAE4]">
                    <h4 className="p-2 text-left text-[#EC3310] font-['Recoleta'] underline">
                        More Info
                    </h4>  
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="assignedNumber"><h6>Assigned Number</h6></label>
                            {editing ? (
                                <input type="text" className="form-control bg-[#e9ecef]" id="assignedNumber" value={form?.assignedNumber || ""} disabled />
                            ) : (
                                <input type="text" className="form-control bg-[#e9ecef]" id="assignedNumber" value={organization.assignedNumber || ""} readOnly/>
                            )}
                        </div>
                        <div className="col">
                            <label htmlFor="prayerPartnersCount"><h6>Prayer Partners Count</h6></label>
                            {editing ? (
                                <input type="text" className="form-control bg-[#e9ecef]" id="prayerPartnersCount" value={auth0PrayerPartnerCount ?? ""} disabled />
                            ) : (
                                <input type="text" className="form-control bg-[#e9ecef]" id="prayerPartnersCount" value={auth0PrayerPartnerCount ?? ""} readOnly />
                            )}
                        </div>
                    </div>
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="serviceStartTimes"><h6>Sunday Service Start Times</h6></label>
                            {editing ? (
                                <input type="text" className={`form-control bg-[#FFEAE4] ${fieldErrors.serviceStartTimes ? "is-invalid" : ""}`} id="serviceStartTimes" maxLength={4000} placeholder="e.g. Sundays at 10:00 AM" value={form?.serviceStartTimes || ""} onChange={(e) => updateField('serviceStartTimes', e.target.value)} />
                            ) : (
                                <input type="text" className="form-control bg-[#FFEAE4]" id="serviceStartTimes" value={organization.serviceStartTimes || ""} readOnly />
                            )}
                            {editing && fieldErrors.serviceStartTimes && <div className="invalid-feedback">{fieldErrors.serviceStartTimes}</div>}
                        </div>
                    </div>
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="churchPrograms"><h6>Church Programs</h6></label>
                            {editing ? (
                                <input type="text" className={`form-control bg-[#FFEAE4] ${fieldErrors.churchPrograms ? "is-invalid" : ""}`} id="churchPrograms" maxLength={4000} placeholder="Describe church programs" value={form?.churchPrograms || ""} onChange={(e) => updateField('churchPrograms', e.target.value)} />
                            ) : (
                                <input type="text" className="form-control bg-[#FFEAE4]" id="churchPrograms" value={organization.churchPrograms || ""} readOnly />
                            )}
                            {editing && fieldErrors.churchPrograms && <div className="invalid-feedback">{fieldErrors.churchPrograms}</div>}
                        </div>
                    </div>
                    <div className="row form-group mb-2 p-2">
                        <div className="col">
                            <label htmlFor="description"><h6>Subscriber Description</h6></label>
                            {editing ? (
                                <textarea className={`form-control bg-[#FFEAE4] ${fieldErrors.description ? "is-invalid" : ""}`} id="description" maxLength={4000} placeholder="Describe the organization" value={form?.description || ""} onChange={(e) => updateField('description', e.target.value)} />
                            ) : (
                                <textarea className="form-control bg-[#FFEAE4]" id="description" value={organization.description || ""} readOnly />
                            )}
                            {editing && fieldErrors.description && <div className="invalid-feedback">{fieldErrors.description}</div>}
                        </div>
                    </div>
                </div>

                {/* Users Section */}
                <div className="p-3">
                    <OrganizationUsers organizationName={organization.name} />
                </div>

                {/* remove body footer controls - controls moved to modal footer */}
            </Modal.Body>
            <Modal.Footer>
                {canEdit && (
                  <>
                    {!editing ? (
                      <Button variant="outline-secondary" onClick={() => setEditing(true)} style={{ marginRight: 8 }}>Edit</Button>
                    ) : (
                      <>
                        <Button variant="primary" onClick={handleSave} style={{ marginRight: 8 }}>Save</Button>
                        <Button variant="secondary" onClick={() => { setEditing(false); setForm({ ...organization }); }} style={{ marginRight: 8 }}>Cancel</Button>
                      </>
                    )}
                  </>
                )}
                <Button variant="outline-secondary" onClick={handleClose}>
                  Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}; 